import type { ScheduleCardColor } from '../ScheduleCard/ScheduleCard.types'
import type {
  SchedulerApptStatus,
  SchedulerBlockRange,
  SchedulerEvent,
  SchedulerFilterState,
  SchedulerView,
} from './Scheduler.types'
import { SCHEDULER_STATUS_BADGE_COLOR } from './Scheduler.types'

export const minutesSinceMidnight = (date: Date): number =>
  date.getHours() * 60 + date.getMinutes()

export const formatHourLabel = (hour: number): string => {
  const h = ((hour + 11) % 12) + 1
  return `${h}:00`
}

export const toDayKey = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const addDays = (date: Date, days: number): Date => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export const addWeeks = (date: Date, weeks: number): Date =>
  addDays(date, weeks * 7)

export const addMonths = (date: Date, months: number): Date => {
  const next = new Date(date)
  const day = next.getDate()
  next.setDate(1)
  next.setMonth(next.getMonth() + months)
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()
  next.setDate(Math.min(day, lastDay))
  return next
}

export const startOfDay = (date: Date): Date => {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

/** Sunday-start week, matching Appointment `firstDayOfWeek={0}`. */
export const startOfWeek = (date: Date): Date => {
  const next = startOfDay(date)
  next.setDate(next.getDate() - next.getDay())
  return next
}

export const startOfMonth = (date: Date): Date => {
  const next = startOfDay(date)
  next.setDate(1)
  return next
}

export const endOfMonth = (date: Date): Date => {
  const next = startOfDay(date)
  next.setMonth(next.getMonth() + 1, 0)
  return next
}

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

export const getWeekDays = (date: Date): Date[] => {
  const start = startOfWeek(date)
  return Array.from({ length: 7 }, (_, index) => addDays(start, index))
}

/** 6×7 grid covering the visible month (includes adjacent-month days). */
export const getMonthGridDays = (date: Date): Date[] => {
  const monthStart = startOfMonth(date)
  const gridStart = startOfWeek(monthStart)
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index))
}

export const formatWeekDayColumnLabel = (date: Date): string => {
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' })
  return `${weekday} ${date.getDate()}`
}

export const formatSchedulerDateLabel = (
  date: Date,
  view: SchedulerView = 'day',
): string => {
  if (view === 'month') {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    })
  }

  if (view === 'week') {
    const days = getWeekDays(date)
    const start = days[0]
    const end = days[6]
    const startLabel = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    const endLabel = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    return `${startLabel} – ${endLabel}`
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export const formatEventTimeRange = (start: Date, end: Date): string => {
  const opts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  }
  return `${start.toLocaleTimeString('en-US', opts)} - ${end.toLocaleTimeString(
    'en-US',
    opts,
  )}`
}

export const getEventLayout = (
  event: SchedulerEvent,
  dayStartHour: number,
  pixelsPerHour: number,
): { top: number; height: number } => {
  const startMinutes = minutesSinceMidnight(event.start)
  const endMinutes = minutesSinceMidnight(event.end)
  const dayStartMinutes = dayStartHour * 60
  const top = ((startMinutes - dayStartMinutes) / 60) * pixelsPerHour
  const height = Math.max(
    ((endMinutes - startMinutes) / 60) * pixelsPerHour,
    pixelsPerHour / 2,
  )
  return { top, height }
}

export type SchedulerEventLaneLayout = {
  lane: number
  laneCount: number
}

/**
 * Pack overlapping events into side-by-side lanes (Appointment week/day style).
 */
export const layoutOverlappingEvents = (
  events: SchedulerEvent[],
): Map<string, SchedulerEventLaneLayout> => {
  const result = new Map<string, SchedulerEventLaneLayout>()
  if (events.length === 0) return result

  const sorted = [...events].sort(
    (a, b) =>
      a.start.getTime() - b.start.getTime() ||
      a.end.getTime() - b.end.getTime() ||
      a.id.localeCompare(b.id),
  )

  const clusters: SchedulerEvent[][] = []
  let currentCluster: SchedulerEvent[] = []
  let clusterEnd = 0

  for (const event of sorted) {
    const start = event.start.getTime()
    const end = event.end.getTime()
    if (currentCluster.length === 0) {
      currentCluster = [event]
      clusterEnd = end
      continue
    }
    if (start < clusterEnd) {
      currentCluster.push(event)
      clusterEnd = Math.max(clusterEnd, end)
    } else {
      clusters.push(currentCluster)
      currentCluster = [event]
      clusterEnd = end
    }
  }
  if (currentCluster.length > 0) clusters.push(currentCluster)

  for (const cluster of clusters) {
    const laneEnds: number[] = []
    const lanes = new Map<string, number>()
    const ordered = [...cluster].sort(
      (a, b) =>
        a.start.getTime() - b.start.getTime() ||
        a.end.getTime() - b.end.getTime() ||
        a.id.localeCompare(b.id),
    )

    for (const event of ordered) {
      const start = event.start.getTime()
      let lane = laneEnds.findIndex((end) => end <= start)
      if (lane === -1) {
        lane = laneEnds.length
        laneEnds.push(event.end.getTime())
      } else {
        laneEnds[lane] = event.end.getTime()
      }
      lanes.set(event.id, lane)
    }

    const laneCount = Math.max(laneEnds.length, 1)
    for (const event of cluster) {
      result.set(event.id, {
        lane: lanes.get(event.id) ?? 0,
        laneCount,
      })
    }
  }

  return result
}

const matchesSearch = (event: SchedulerEvent, query: string): boolean => {
  if (!query) return true
  return (
    event.patientName.toLowerCase().includes(query) ||
    (event.appointmentType ?? '').toLowerCase().includes(query) ||
    (event.providerName ?? '').toLowerCase().includes(query) ||
    (event.info?.provider ?? '').toLowerCase().includes(query)
  )
}

export const filterEventsForDay = (
  events: SchedulerEvent[],
  date: Date,
  searchValue = '',
): SchedulerEvent[] => {
  const query = searchValue.trim().toLowerCase()
  return events.filter(
    (event) => isSameDay(event.start, date) && matchesSearch(event, query),
  )
}

export const filterEventsForRange = (
  events: SchedulerEvent[],
  start: Date,
  end: Date,
  searchValue = '',
): SchedulerEvent[] => {
  const query = searchValue.trim().toLowerCase()
  const rangeStart = startOfDay(start).getTime()
  const rangeEnd = startOfDay(end).getTime()
  return events.filter((event) => {
    const day = startOfDay(event.start).getTime()
    if (day < rangeStart || day > rangeEnd) return false
    return matchesSearch(event, query)
  })
}

export const groupEventsByDayKey = (
  events: SchedulerEvent[],
): Record<string, SchedulerEvent[]> => {
  return events.reduce<Record<string, SchedulerEvent[]>>((acc, event) => {
    const key = toDayKey(event.start)
    if (!acc[key]) acc[key] = []
    acc[key].push(event)
    return acc
  }, {})
}

export type SchedulerProviderGroup = {
  providerName: string
  count: number
  color?: ScheduleCardColor
}

export type SchedulerDayProviderSummary = {
  totalAppointments: number
  providers: SchedulerProviderGroup[]
}

export const getEventProviderName = (event: SchedulerEvent): string =>
  event.providerName?.trim() || event.info?.provider?.trim() || 'Unassigned'

export const groupEventsByDayAndProvider = (
  events: SchedulerEvent[],
): Record<string, SchedulerDayProviderSummary> => {
  const byDay = groupEventsByDayKey(events)

  return Object.entries(byDay).reduce<
    Record<string, SchedulerDayProviderSummary>
  >((acc, [dayKey, dayEvents]) => {
    const providerMap = new Map<string, SchedulerProviderGroup>()

    dayEvents.forEach((event) => {
      const providerName = getEventProviderName(event)
      const existing = providerMap.get(providerName)
      if (existing) {
        existing.count += 1
        if (!existing.color && event.color) existing.color = event.color
        return
      }
      providerMap.set(providerName, {
        providerName,
        count: 1,
        color: event.color,
      })
    })

    acc[dayKey] = {
      totalAppointments: dayEvents.length,
      providers: Array.from(providerMap.values()),
    }
    return acc
  }, {})
}

export const shiftSchedulerDate = (
  date: Date,
  view: SchedulerView,
  direction: -1 | 1,
): Date => {
  if (view === 'week') return addWeeks(date, direction)
  if (view === 'month') return addMonths(date, direction)
  return addDays(date, direction)
}

export const applySchedulerFilters = (
  events: SchedulerEvent[],
  filters: SchedulerFilterState,
  searchValue = '',
): SchedulerEvent[] => {
  const query = searchValue.trim().toLowerCase()
  const providerSet = new Set(filters.providerIds)
  const roomSet = new Set(filters.roomIds)
  const statusSet = new Set(filters.statuses)
  // Empty category = no narrowing (SearchWithFilter reset / default).
  const filterProviders = providerSet.size > 0
  const filterRooms = roomSet.size > 0
  const filterStatuses = statusSet.size > 0

  return events.filter((event) => {
    if (!matchesSearch(event, query)) return false
    if (
      filterProviders &&
      event.providerId &&
      !providerSet.has(event.providerId)
    ) {
      return false
    }
    if (filterRooms && !roomSet.has(event.columnId)) return false
    if (filterStatuses && event.status && !statusSet.has(event.status)) {
      return false
    }
    return true
  })
}

export const getNextSchedulerStatus = (
  status?: SchedulerApptStatus,
): SchedulerApptStatus | null => {
  switch (status) {
    case 'Unconfirmed':
      return 'Confirmed'
    case 'Confirmed':
    case 'Late':
    case 'No-show':
      return 'Check-in'
    case 'Check-in':
      return 'In-treatment'
    case 'In-treatment':
      return 'Check-out'
    case 'Check-out':
      return 'Complete'
    default:
      return null
  }
}

export const withUpdatedEventStatus = (
  event: SchedulerEvent,
  status: SchedulerApptStatus,
): SchedulerEvent => ({
  ...event,
  status,
  info: event.info
    ? {
        ...event.info,
        status,
        statusColor: SCHEDULER_STATUS_BADGE_COLOR[status],
      }
    : event.info,
})

export const snapMinutes = (minutes: number, step = 15): number =>
  Math.round(minutes / step) * step

export const minutesToDateOnDay = (
  day: Date,
  minutesFromMidnight: number,
): Date => {
  const next = startOfDay(day)
  next.setMinutes(minutesFromMidnight)
  return next
}

export const eventOverlapsBlock = (
  start: Date,
  end: Date,
  columnId: string,
  blocks: SchedulerBlockRange[],
): boolean => {
  const startHour =
    start.getHours() + start.getMinutes() / 60 + start.getSeconds() / 3600
  const endHour =
    end.getHours() + end.getMinutes() / 60 + end.getSeconds() / 3600

  return blocks.some((block) => {
    if (block.columnId && block.columnId !== columnId) return false
    return startHour < block.endHour && endHour > block.startHour
  })
}

/** True when the appointment start is before the current moment. */
export const isSchedulerTimeInPast = (
  start: Date,
  now: Date = new Date(),
): boolean => start.getTime() < now.getTime()

export type SchedulerColumnHit = {
  index: number
  /** Left edge relative to the grid element. */
  left: number
  width: number
}

/**
 * Resolve which day/room column contains a pointer, using real DOM geometry
 * (avoids drift from assuming equal `rect.width / columnCount` widths).
 */
export const getSchedulerColumnHit = (
  grid: HTMLElement,
  clientX: number,
): SchedulerColumnHit | null => {
  const columns = grid.querySelectorAll<HTMLElement>('.ds-scheduler__column')
  if (columns.length === 0) return null

  const gridRect = grid.getBoundingClientRect()

  for (let index = 0; index < columns.length; index += 1) {
    const columnRect = columns[index].getBoundingClientRect()
    if (clientX >= columnRect.left && clientX < columnRect.right) {
      return {
        index,
        left: columnRect.left - gridRect.left,
        width: columnRect.width,
      }
    }
  }

  // Outside the strip: snap to nearest column by center distance.
  let bestIndex = 0
  let bestDistance = Number.POSITIVE_INFINITY
  for (let index = 0; index < columns.length; index += 1) {
    const columnRect = columns[index].getBoundingClientRect()
    const center = (columnRect.left + columnRect.right) / 2
    const distance = Math.abs(clientX - center)
    if (distance < bestDistance) {
      bestDistance = distance
      bestIndex = index
    }
  }

  const bestRect = columns[bestIndex].getBoundingClientRect()
  return {
    index: bestIndex,
    left: bestRect.left - gridRect.left,
    width: bestRect.width,
  }
}

export const updateEventInfoDateTime = (
  event: SchedulerEvent,
): SchedulerEvent => {
  const dateTime = event.start.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  return {
    ...event,
    info: event.info ? { ...event.info, dateTime } : event.info,
  }
}
